<template>
  <div class="attribution-panel">
    <b-field class="stacked-label" style="margin-bottom: 5px">
      <template v-slot:label>
        <span class="field-title">{{ $t('attribution_partner_short') }} *</span>
        <b-tooltip type="is-dark" :label="attributionPartnerHelp" multilined>
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
        <span class="field-hint">{{ $t('attribution_partner_hint') }}</span>
      </template>
      <b-field class="partner-picker">
        <b-radio-button v-model="offer.attribution_provider" native-value="AppsFlyer" class="attribution-button"
                        :disabled="disabled">
          <img src="images/attribution/appsflyer.png" alt="AppsFlyer" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-if="offer.platform_id !== 3" v-model="offer.attribution_provider" native-value="Kochava"
                        class="attribution-button" :disabled="disabled">
          <img src="images/attribution/kochava.png" alt="Kochava" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-model="offer.attribution_provider" native-value="Adjust" class="attribution-button"
                        :disabled="disabled">
          <img src="images/attribution/adjust.png" alt="Adjust" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-if="offer.platform_id !== 3" v-model="offer.attribution_provider" native-value="Branch"
                        class="attribution-button" :disabled="disabled">
          <img src="images/attribution/branch.png" alt="Branch" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-model="offer.attribution_provider" native-value="Singular" class="attribution-button"
                        :disabled="disabled">
          <img src="images/attribution/singular.png" alt="Singular" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-if="offer.platform_id !== 3" v-model="offer.attribution_provider" native-value="TrafficGuard"
                        class="attribution-button trafficguard" :disabled="disabled">
          <img src="images/attribution/trafficguard.svg" alt="TrafficGuard" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-if="offer.platform_id !== 3" v-model="offer.attribution_provider" native-value="Tenjin"
                        class="attribution-button" :disabled="disabled">
          <img src="images/attribution/tenjin.svg" alt="Tenjin" class="attribution-logo">
        </b-radio-button>
        <b-radio-button v-if="offer.platform_id !== 3" v-model="offer.attribution_provider" native-value="Interceptd"
                        class="attribution-button" :disabled="disabled">
          <img src="images/attribution/interceptd.svg" alt="Interceptd" class="attribution-logo">
        </b-radio-button>
      </b-field>
    </b-field>

    <b-field v-if="offer.platform_id !== 3">
      <template v-slot:label>
        {{ $t('click_url') }} *
        <b-tooltip type="is-dark" :label="$t('click_url_tooltip')" multilined>
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
      </template>
      <div class="control">
        <div v-if="offer.click_url && !show_click_url" class="url-summary">
          <span class="url-short">{{ offer.click_url }}</span>
          <button type="button" class="url-action" @click="copyUrl(offer.click_url)">{{ $t('copy_url') }}</button>
          <button type="button" class="url-action" @click="show_click_url = true">{{ $t('show_full_url') }}</button>
        </div>
        <div v-else>
          <b-input v-model="offer.click_url" :placeholder="$t('click_url')" @blur="validateClickUrl" type="textarea"
                   rows="8" required :disabled="disabled"></b-input>
          <button v-if="offer.click_url" type="button" class="url-action top-spacer-10" @click="show_click_url = false">
            {{ $t('hide_full_url') }}
          </button>
        </div>
        <div v-if="click_url_reserved_parameters && click_url_reserved_parameters.length > 0" class="red top-spacer-10">
          {{ $t('contains_reserved_parameter') }} {{ click_url_reserved_parameters }}.
          {{ $t('system_will_overwrite_reserved_parameters') }}
        </div>
      </div>
    </b-field>

    <b-field>
      <template v-slot:label>
        {{ $t('impression_url') }}
        <b-tooltip type="is-dark" :label="$t('impression_url_tooltip')" multilined>
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
      </template>
      <div class="control">
        <div v-if="offer.impression_url && !show_impression_url" class="url-summary">
          <span class="url-short">{{ offer.impression_url }}</span>
          <button type="button" class="url-action" @click="copyUrl(offer.impression_url)">{{ $t('copy_url') }}</button>
          <button type="button" class="url-action" @click="show_impression_url = true">{{ $t('show_full_url') }}</button>
        </div>
        <div v-else>
          <b-input v-model="offer.impression_url" :placeholder="$t('impression_url')" @blur="validateImpressionUrl"
                   type="textarea" rows="8" :disabled="disabled"></b-input>
          <button v-if="offer.impression_url" type="button" class="url-action top-spacer-10"
                  @click="show_impression_url = false">{{ $t('hide_full_url') }}
          </button>
        </div>
        <div class="top-space"></div>

        <span v-if="offer.attribution_provider === 'AppsFlyer' && offer.platform_id === 3" class="top-spacer-10">
                    Note that only AppsFlyer OneLink URLs are supported for Connected TV campaigns.<br/>
                </span>

        <span v-if="offer.attribution_provider === 'AppsFlyer'"
              class="top-spacer-10">{{ $t('make_sure_to_turn_on') }} <a
            href="https://support.appsflyer.com/hc/en-us/articles/4410395957521-Set-up-an-integrated-partner#permissions-tab"
            target="_blank">{{ $t('ad_network_permissions') }}</a></span>

        <span v-if="offer.attribution_provider === 'AppsFlyer' && offer.platform_id === 3" class="top-spacer-10">
                    {{ $t('and_that') }} <a
            href="https://support.appsflyer.com/hc/en-us/articles/6966260647185-Cross-platform-OneLink-links#:~:text=cross%2Dplatform%20impressions%3A-,In%20AppsFlyer%2C%20from%20the%20side%20menu%2C%20select%20Collaborate%20%3E%20Active,Click%20Save%20integration"
            target="_blank">{{ $t('crossdevice_attribution_is_enabled') }}.</a><br/>
                    <a href="https://support.appsflyer.com/hc/en-us/articles/6966260647185-Set-up-cross-platform-links-for-multi-device-campaigns"
                       target="_blank">Help with tracking links</a><br/>
                </span>

        <span v-if="offer.attribution_provider === 'Adjust' && offer.platform_id === 3" class="top-spacer-10">
                    <a href="https://help.adjust.com/en/article/connected-tv-ad-to-mobile-measurement" target="_blank">Connected TV to mobile measurement</a><br/>
                    <a href="https://help.adjust.com/en/article/link-structure-ad-network#set-your-link-use-case"
                       target="_blank">Help with tracking links</a><br/>
                </span>

        <span v-if="offer.attribution_provider === 'Singular' && offer.platform_id === 3" class="top-spacer-10">
                    <a href="https://support.singular.net/hc/en-us/articles/13581281810331-Mobile-Attribution-for-Connected-TV-CTV-FAQ"
                       target="_blank">Mobile Attribution for Connected TV (CTV) FAQ</a><br/>
                </span>

        <span v-if="impression_url_reserved_parameters && impression_url_reserved_parameters.length > 0"
              class="red top-spacer-10">{{ $t('contains_reserved_parameter') }} {{ impression_url_reserved_parameters }}. {{ $t('system_will_overwrite_reserved_parameters') }}</span>

        <!-- Link analysis REMOVE v-if for full release!-->
        <div class="top-spacer-10" v-if="offer.attribution_provider === 'AppsFlyer' || offer.attribution_provider === 'Adjust' || offer.attribution_provider === 'Kochava' || offer.attribution_provider === 'Branch' || offer.attribution_provider === 'Singular'">
          <b-button
              size="is-small"
              type="is-info"
              :loading="link_analysis_loading"
              :disabled="disabled || link_analysis_loading || (!offer.click_url && !offer.impression_url)"
              :label="$t('ai_link_analysis')"
              @click="analyzeLinks">
          </b-button>
        </div>

        <!-- Link analysis result -->
        <div v-if="link_analysis_answer" class="top-spacer-10">
          <b-message type="is-info" size="is-small">
            <div class="link-analysis-answer">{{ link_analysis_answer }} </div>
          </b-message>
        </div>
        <div v-if="link_analysis_error" class="top-spacer-10 red">
          {{ link_analysis_error }}
        </div>
      </div>
    </b-field>

    <b-collapse :open="false" position="is-bottom" aria-id="attributionProviderHelp"
                v-if="offer.attribution_provider && offer.platform_id !== 3">
      <a slot="trigger" slot-scope="props" aria-controls="attributionProviderHelp">
        <b-icon :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
        {{ !props.open ? 'Help with tracking URLs' : 'Hide' }}
      </a>
      <p><b>How to generate {{ offer.attribution_provider }} tracking URLs</b></p><br/>
      <div v-if="offer.attribution_provider === 'AppsFlyer'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>From your app dashboard, navigate to Integrated Partners.</li>
          <li>Search for and select "DemandScale".</li>
          <li>On the Integration tab, toggle on Activate Partner.</li>
          <li>Make sure Enable View-Through attribution is toggled on.</li>
          <li v-if="offer.platform_id === 2">Make sure that the "Advanced Privacy (for iOS 14.5+ and later)" is set to
            OFF.
          </li>
          <li>Click Save Integration.</li>
        </ol>

        <br/>
        <p><b>Create tracking URLs</b></p>
        <ol>
          <li>If not already on the DemandScale Integration page, navigate to Integrated Partners, search for and select
            "DemandScale".
          </li>
          <li>Go to the Attribution link tab.</li>
          <li>Select the parameters that you want to be passed along on the attribution links, if any. These can be used
            for analysis later.
          </li>
          <li>Make sure "Click-Through Lookback Window" is set to 7 days or higher.</li>
          <li>Make sure "View-Through Lookback Window" is set to 1 day.</li>
          <li>Copy and paste the Click Attribution URL into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the Impression Attribution URL into the {{ $t('impression_url') }} field above.</li>
        </ol>

        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like.
          Don't copy and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.AppsFlyer.click.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.AppsFlyer.click.ios') }}
        </p>
        <br/>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.AppsFlyer.impression.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.AppsFlyer.impression.ios') }}
        </p>
        <br/>

        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &af_sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &af_sub2={scrid}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace af_sub1 and af_sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        <b>Reserved parameters:</b><br/>
        The following parameters are reserved and should not be changed in tracking URLs:<br/>
        <ul>
          <li>af_c_id</li>
          <li>af_siteid</li>
          <li>af_sub_siteid</li>
          <li>idfa</li>
          <li>af_model</li>
          <li>af_ua</li>
          <li>af_lang</li>
          <li>af_ip</li>
          <li>af_ref</li>
          <li>af_channel</li>
          <li>af_ad_id</li>
          <li>af_adset_id</li>
          <li>af_os</li>
        </ul>
        <br/>

        For a more {{ offer.attribution_provider }} documentation, click these links below:<br/>
        <a href="https://support.appsflyer.com/hc/en-us/articles/207033816#partner-setup" target="_">Integrated partners
          setup guide</a><br/>
        <a href="https://support.appsflyer.com/hc/en-us/articles/207447163-Attribution-link-structure-and-parameters"
           target="_">Attribution link structure and parameters</a><br/>
        <a href="https://support.appsflyer.com/hc/en-us/articles/210084473-View-through-attribution-guide" target="_">View-through
          attribution guide</a>
      </div>

      <div v-if="offer.attribution_provider === 'Kochava'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Navigate to APPS &amp; ASSETS.</li>
          <li>Click Partner Configuration, then Add a Configuration.</li>
          <li>Select DemandScale under MEDIA PARTNER.</li>
          <li>Click GO.</li>
        </ol>

        <br/>
        <p><b>Create tracking URLs</b></p>
        <ol>
          <li>Navigate to All Apps.</li>
          <li>Select Campaign Manager from your app menu.</li>
          <li>Click Add a Tracker.</li>
          <li>Select DemandScale under MEDIA PARTNER.</li>
          <li>Add the parameters that you want to be passed along on the attribution links, if any. These can be used
            for analysis later.
          </li>
          <li>Click Save.</li>
          <li>Copy and paste the CLICK URL into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the IMPRESSION URL into the {{ $t('impression_url') }} field above.</li>
        </ol>

        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Kochava.click') }}</p>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Kochava.impression') }}</p>
        <br/>

        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &sub2={scrid}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace sub1 and sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        For a more documentation on {{ offer.attribution_provider }} tracking URLs, click the link below:<br/>
        <a href="https://support.kochava.com/campaign-management/create-an-install-campaign/" target="_">Create an
          Install Campaign</a>
      </div>

      <div v-if="offer.attribution_provider === 'Adjust'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Navigate to Partner Setup.</li>
          <li>Click the ADD PARTNERS button.</li>
          <li>Search for "DemandScale" in the search field and press the + button.</li>
        </ol>
        <br/>

        <p><b>Make sure View through tracking is enabled</b></p>
        <ol>
          <li>Find your app and select your app options caret (^).</li>
          <li>Select All Settings (gear icon).</li>
          <li>Select Attribution.</li>
          <li>Turn on Device Matching under Impression Based.</li>
          <li>Turn on Fingerprinting under Impression Based.</li>
          <li>Click SAVE.</li>
        </ol>

        <br/>
        <p><b>Create your tracking URLs</b></p>
        <ol>
          <li>Navigate to Trackers.</li>
          <li>Click the NEW TRACKER button.</li>
          <li>Enter a tracker name.</li>
          <li>Click QUICK CREATE (or CUSTOMIZE if you want to add custom parameters).</li>
          <li>Copy and paste the Click URL into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the Impression URL into the {{ $t('impression_url') }} field above.</li>
        </ol>
        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.Adjust.click.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.Adjust.click.ios') }}
        </p>
        <br/>
        <br/>

        <b>Example impression tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.Adjust.impression.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.Adjust.impression.ios') }}
        </p>
        <br/>
        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &sub2={scrid}<br/>
          Subsite: <b>{sub2}</b><br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace sub1 and sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        <b>Reserved parameters:</b><br/>
        The following parameters are reserved and should not be changed in tracking URLs:<br/>
        <ul>
          <li>os_name</li>
          <li>install_callback</li>
        </ul>
        <br/>

        For a more documentation on {{ offer.attribution_provider }} tracking URLs, click the link below:<br/>
        <a href="http://help.adjust.com/tracking/attribution/tracker-urls/basic-tracker-setup" target="_">Basic tracker
          setup</a>
      </div>

      <div v-if="offer.attribution_provider === 'Branch'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Click Ads from the menu, then Partner Management.</li>
          <li>Enter DemandScale in the search field and select the DemandScale partner.</li>
          <li>Click Save &amp; Enable.</li>
        </ol>

        <br/>
        <p><b>Create your tracking URLs</b></p>
        <ol>
          <li>Click the Create Link button.</li>
          <li>Select "App Install or App Engagement" and click Continue.</li>
          <li>Enter a name for your link, make sure DemandScale is selected as the Ad Partner, and App Only is selected
            as the ad format.
          </li>
          <li>Enter Channel, Campaign, and Analytics Tags if desired. These can be used for analysis later.</li>
          <li>Click Create Link Now.</li>
          <li>Copy and paste the Click Tracking Link into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the Impression Tracking Link into the {{ $t('impression_url') }} field above.</li>
        </ol>

        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Branch.click') }}
        </p>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Branch.impression') }}
        </p>
        <br/>

        <p>
          <b>%243p=a_sift_media</b> is still supported for existing campaigns, but new campaigns need to use our <b>%243p=a_demandscale</b>
          account.
        </p>
        <br/>

        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &sub2={scrid}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace sub1 and sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        For a more documentation on {{ offer.attribution_provider }} tracking URLs, click these links below:<br/>
        <a href="https://docs.branch.io/deep-linked-ads/branch-universal-ads-mobile-tracking/" target="_">Universal Ads
          Basics</a><br/>
        <a href="https://docs.branch.io/deep-linked-ads/branch-universal-ads-advanced/" target="_">Universal Ads
          Advanced</a>
      </div>

      <div v-if="offer.attribution_provider === 'Singular'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Navigate to Partner Configuration.</li>
          <li>Search for and select "DemandScale".</li>
          <li>Select your App.</li>
          <li>Make sure that "Send postbacks to DemandScale on all Installs" is checked.</li>
          <li>Set the "Click-through attribution lookback window" to 7 days or higher.</li>
          <li>Make sure that "Enable View-through Postbacks" is checked.</li>
        </ol>

        <br/>
        <p><b>Create your tracking URLs</b></p>
        <ol>
          <li>Navigate to Create Link</li>
          <li>Enter the required information for your app.</li>
          <li>Select DemandScale as the partner option.</li>
          <li>Click Generate Link.</li>
          <li>Copy and paste the Click URL into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the Impression URL into the {{ $t('impression_url') }} field above.</li>
        </ol>
        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Singular.click') }}
        </p>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Singular.impression') }}
        </p>
        <br/>

        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &sub2={scrid}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace sub1 and sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        For a more documentation on {{ offer.attribution_provider }} tracking URLs, click these links below:<br/>
        <a href="https://support.singular.net/hc/en-us/articles/360030934212" target="_">Singular Links FAQ</a><br/>
        <a href="https://support.singular.net/hc/en-us/articles/360031365971" target="_">Singular Links Best Practices
          and Examples</a>
      </div>

      <div v-if="offer.attribution_provider === 'TrafficGuard'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Go to Traffic Control > Integrated partners.</li>
          <li>Click 'ADD NEW' on the Integrated partners page.</li>
          <li>On the 'Select a Partner', choose DemandScale.</li>
          <li>Select a property that you would like to see partners on.</li>
          <li>On the partner page, set configurations as you wish.</li>
          <li>Click 'SAVE'. The partner name will appear as part of your 'Integrated Partners' list.</li>
        </ol>
        <br/>

        <p><b>Create your tracking URLs</b></p>
        <ol>
          <li>Go to Traffic Control > Integrated partners</li>
          <li>On Integrated partners, search and select DemandScale.</li>
          <li>Select your campaign in the Generate tracking links section.</li>
          <li>Copy and paste the Click URL into the {{ $t('click_url') }} field above.</li>
          <li>Copy and paste the Impression URL into the {{ $t('impression_url') }} field above.</li>
          <li>Click 'SAVE'</li>
        </ol>
        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.TrafficGuard.click') }}
        </p>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.TrafficGuard.impression') }}
        </p>
        <br/>

        For a more {{ offer.attribution_provider }} documentation, click these links below:<br/>
        <a href="https://help.trafficguard.ai/knowledge/measurement-url" target="_">Measurement URL</a><br/>
        <a href="https://help.trafficguard.ai/knowledge/impression-measurement-url" target="_">Impression Measurement
          URL</a><br/>
        <a href="https://help.trafficguard.ai/en/knowledge/partner-postback-macros" target="_">Partner Postback
          Macros</a><br/>
      </div>

      <div v-if="offer.attribution_provider === 'Tenjin'">
        <p><b>Enable DemandScale as a partner</b></p>
        <ol>
          <li>Log into your {{ offer.attribution_provider }} account.</li>
          <li>Go to the Channels tab and click Add a Channel</li>
          <li>Select ‘DemandScale’</li>
          <li>Name the account</li>
          <li>Set the attribution window for clicks (the default is 7 days, leave blank to keep the default)</li>
        </ol>
        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. You can
          see these URLs on the campaign page. Don't copy and paste these examples, use your generated URLs from
          {{ offer.attribution_provider }}.</p><br/>

        <b>Example click tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.Tenjin.click.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.Tenjin.click.ios') }}
        </p>
        <br/>

        <b>Example impression tracking URL:</b><br/>
        <p v-if="offer.platform_id === 1" class="link">
          {{ $t('examples.Tenjin.impression.android') }}
        </p>
        <p v-else-if="offer.platform_id === 2" class="link">
          {{ $t('examples.Tenjin.impression.ios') }}
        </p>
        <br/>

        For a more {{ offer.attribution_provider }} documentation, click these links below:<br/>
        <a href="https://docs.tenjin.com/docs/campaign-setup" target="_">Campaign Setup</a><br/>
        <a href="https://docs.tenjin.com/docs/callback-setup" target="_">Callback Setup</a><br/>
        <a href="https://docs.tenjin.com/docs/callback-macros" target="_">Callback Macros</a><br/>
      </div>

      <div v-if="offer.attribution_provider === 'Interceptd'">
        <p><b>Enable DemandScale as a partner</b></p>
        <div style="margin-left: 10px">
          1. Log into your {{ offer.attribution_provider }} account.<br/>
          2. Navigate to Partner Configuration.<br/>
          3. Search for and select "DemandScale".<br/>
          4. Select your App.<br/>
          5. Make sure that "Send postbacks to DemandScale on all Installs" is checked.<br/>
          6. Set the "Click-through attribution lookback window" to 7 days or higher.<br/>
          7. Make sure that "Enable View-through Postbacks" is checked.<br/>
        </div>

        <br/>
        <p><b>Create your tracking URLs</b></p>
        <div style="margin-left: 10px">
          1. Navigate to Create Link<br/>
          2. Enter the required information for your app.<br/>
          3. Select DemandScale as the partner option.<br/>
          4. Click Generate Link.<br/>
          5. Copy and paste the Click URL into the {{ $t('click_url') }} field above.<br/>
          6. Copy and paste the Impression URL into the {{ $t('impression_url') }} field above.<br/>
        </div>
        <br/>
        <p>Here are some examples of what the {{ offer.attribution_provider }} tracking URLs might look like. Don't copy
          and paste these examples, use your generated URLs from {{ offer.attribution_provider }}.</p><br/>

        <p>
          <b>Optional macros:</b><br/>
          Source App Name: <b>{sub4}</b>, e.g. &sub1={sub4}<br/>
          Creative ID: <b>{scrid}</b>, e.g. &sub2={scrid}<br/>
          Ad Placement Name: <b>{app_name}</b>, e.g. &af_adset={app_name}<br/>
          Creative Name: <b>{creative_name}</b>, e.g. &af_ad={creative_name}<br/>
          RTB Exchange ID: <b>{exchange_id}</b>, e.g. &af_channel={exchange_id}<br/>
          RTB Exchange Name: <b>{exchange}</b>, e.g. &sub2={exchange}<br/>
          Bundle ID: <b>{sub3}</b>, e.g. &af_siteid={sub3}<br/>
          Device Model: <b>{device_model}</b>, e.g. &af_model={device_model}<br/>
          Carrier: <b>{carrier}</b>, e.g. &af_sub5={carrier}<br/>
          Impression Type: <b>{impression_type}</b>, e.g. &af_sub6={impression_type}<br/>
          Country: <b>{country}</b>, e.g. &af_sub7={country}<br/>
          You may replace sub1 and sub2 with the parameter names where you want to receive these values.
        </p>
        <br/>

        <b>Example click tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Interceptd.click') }}
        </p>
        <br/>
        <b>Example impression tracking URL:</b><br/>
        <p class="link">
          {{ $t('examples.Interceptd.impression') }}
        </p>
        <br/>
      </div>

      <div style="margin-bottom: 20px"></div>
    </b-collapse>

    <b-collapse :open="false" position="is-bottom" aria-id="attributionTestingHelp"
                v-if="offer.attribution_provider && offer.platform_id !== 3">
      <a slot="trigger" slot-scope="props" aria-controls="attributionTestingHelp">
        <b-icon :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
        {{ !props.open ? 'Help with testing attribution' : 'Hide' }}
      </a>
      <div class="top-space">
        <p><b>How to test attribution</b></p><br/>

        <p>It's a good idea to test your click tracking URL to make sure the attribution works. First, make sure this
          campaign has been saved with a click tracking URL and that status is set to Active.
          Then you'll need a test device that has never installed the app you are advertising, or has been factory
          reset, since attribution is only done on the first install.
          Finally, you'll need a test link. If you use the form below we'll generate one for you and email it to the
          address you provided when you signed up.</p>
        <br/>

        <p v-if="offer.platform_id === 2">
          You will need to provide the Apple IDFA of your test device. This is so that when your test device installs
          the app, the attribution will be linked back
          to your account. The IDFA on iOS devices is hidden from users by Apple by default but can be retrieved with
          third-party apps. There is a wide selection of apps
          with the capability on the App Store. Enter your IDFA in the field below and click the
          {{ $t('generate_test_link') }} button.
        </p>
        <p v-else>
          You will need to provide the Android Advertising ID of your test device. This is so that when your test device
          installs the app, the attribution will be linked
          back to your account. To find your Android Advertising ID, open the Google Settings app on your Android device
          and click on “Ads.” Your Advertising Identifier will be
          listed at the bottom of the screen. Enter your Android Advertising ID in the field below and click the
          {{ $t('generate_test_link') }} button.</p>
        <br/>

        <b-field v-if="offer.platform_id === 2">
          <template v-slot:label>{{ $t('idfa') }} *</template>
          <div class="test-link-row">
            <div class="test-link-input">
              <b-input v-model="device_id" :placeholder="$t('idfa')" type="text"></b-input>
            </div>
            <b-button @click="generateTestLink">{{ $t('generate_test_link') }}</b-button>
          </div>
        </b-field>
        <b-field v-else>
          <template v-slot:label>{{ $t('aaid') }} *</template>
          <div class="test-link-row">
            <div class="test-link-input">
              <b-input v-model="device_id" :placeholder="$t('aaid')" type="text"></b-input>
            </div>
            <b-button @click="generateTestLink">{{ $t('generate_test_link') }}</b-button>
          </div>
        </b-field>

        <ErrorMessages :errors="errors"></ErrorMessages>

        <div id="testLink">
          <b-field>
            <template v-slot:label>{{ $t('test_link') }}</template>
            <b-input v-model="test_click_url" readonly="true"></b-input>
          </b-field>

          <p>You should have received an email containing this test link. If not, you can copy the link from here and
            send it to your device yourself.
            To test, click this link from your test device, then install and open the app. After the app has been
            successfully installed and opened,
            go back to the <a href="dashboard.html">DemandScale Dashboard</a> and make sure you see the install there.
            There may be a delay, so check back later if you don't see it immediately.</p>
        </div>
        <br/>
      </div>
    </b-collapse>
  </div>
</template>

<style>
#testLink {
  visibility: hidden;
}

.attribution-panel {
  margin-bottom: 8px;
  min-width: 0;
  max-width: 100%;
}

.attribution-panel .field,
.attribution-panel .control,
.attribution-panel .textarea {
  min-width: 0;
  max-width: 100%;
}

.partner-picker.field {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.partner-picker > .control {
  flex: 0 0 auto;
  width: auto;
  margin: 0;
}

.url-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.url-short {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 8px 10px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background: #fff;
  color: #363636;
}

.url-action {
  flex: 0 0 auto;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  color: #353fff;
  cursor: pointer;
}

.link {
  word-wrap: break-word;
  color: blue;
}

.top-spacer-10 {
  margin-top: 10px;
}

.top-space {
  padding-top: 20px;
}

.attribution-panel .collapse-content {
  max-width: 100%;
  min-width: 0;
}

.test-link-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.test-link-row .test-link-input {
  flex: 1 1 220px;
  min-width: 0;
  max-width: 100%;
}

.test-link-row .test-link-input .control,
.test-link-row .test-link-input .input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.test-link-row .button {
  flex: 0 1 auto;
  max-width: 100%;
  height: auto;
  white-space: normal;
}

.attribution-button {
  flex: 0 0 auto;
  width: auto;
}

.attribution-button .button {
  width: auto;
  height: 40px;
  padding: 4px 10px;
}

img.attribution-logo {
  display: block;
  height: 22px;
  width: auto;
  max-width: 96px;
  object-fit: contain;
}

.trafficguard .button {
  background-color: #323f8a;
}

.trafficguard .button.is-primary {
  background-color: #00c4a7;
}

.link-analysis-answer {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>

<script>
import ErrorMessages from "../components/ErrorMessages";
import session from "../session";

// Mirrors demandscale-api BillingTypeEnum
const BillingTypeEnum = {
  CPI: { id: 1 },
  CPA: { id: 4 },
  CPM: { id: 6 }
};

const model = {
  device_id: null,
  test_click_url: null,
  click_url_reserved_parameters: null,
  impression_url_reserved_parameters: null,
  link_analysis_loading: false,
  link_analysis_answer: null,
  link_analysis_error: null,
  show_click_url: false,
  show_impression_url: false,
  errors: []
};

function detectAttributionProvider(url, successHandler, errorHandler) {
  if (url) {
    const data = {
      url: url
    };
    session.post('detect_attribution_provider', data, successHandler, errorHandler, []);
  }
}

// Mirrors AttributionService.superuserExemptParameters. Gold is not exempt.
const superuserExemptParameters = {
  AppsFlyer: ['af_engagement_type']
};

function isSuperuserExempt(isSuperUser, provider, key) {
  if (!isSuperUser) {
    return false;
  }
  const keys = superuserExemptParameters[provider] || [];
  return keys.indexOf(key) !== -1;
}

function ensureAppsFlyerEngagedViewForOdtPid(urlStr, isSuperUser) {
  if (
      isSuperuserExempt(isSuperUser, 'AppsFlyer', 'af_engagement_type') ||
      !/[?&]pid=onedigitalturbine_int(?:&|$)/.test(urlStr)
  ) {
    return urlStr;
  }

  return ensureParam(urlStr, 'af_engagement_type', 'engaged_view');
}

function normalizeSingleTapClickUrl(urlStr) {
  if (
      !/\/\/delivers\.dtignite\.com/i.test(urlStr) ||
      !/\/clickAd\.jsp(?:\?|$)/i.test(urlStr)
  ) {
    return urlStr;
  }

  // urlStr = ensureParam(urlStr, 'tsti', 'true');
  urlStr = ensureParam(urlStr, 'enablePostInstallAutoLaunch', 'true');

  return urlStr
}

function ensureParam(urlStr, key, value) {
  let result = urlStr;
  const re = new RegExp(`([?&])${key}=[^&]*`);

  if (re.test(result)) {
    // Replace existing value
    return result.replace(re, `$1${key}=${value}`);
  }

  // Append new param
  const sep = result.includes('?') ? '&' : '?';
  return `${result}${sep}${key}=${value}`;
}

function checkForUrlReservedParameters(url, platform_id, successHandler, errorHandler) {
  if (url) {
    const data = {
      platform_id: platform_id,
      url: url
    };
    session.post('contains_reserved_parameters', data, successHandler, errorHandler, []);
  }
}

export default {
  props: ['offer', 'disabled', 'isSuperUser'],

  components: {
    ErrorMessages
  },

  data() {
    return model;
  },

  computed: {
    attributionPartnerHelp() {
      const help = this.$t('attribution_partner_tooltip');
      if (this.offer.platform_id === 3) {
        return help;
      }
      return help + ' ' + this.$t('different_attribution_partner') + ' ' + this.$t('contact_us') + '.';
    },
    exampleClickUrl() {
      const provider = this.offer.attribution_provider;
      if (!provider) return null;

      // Access $t('examples') – assuming it's an object
      const examples = this.$t('examples');
      if (!examples || typeof examples !== 'object' || !examples[provider]) {
        return null;
      }

      if (this.providerHasPlatformDifferences(provider)) {
        const platformKey = this.offer.platform_id === 1 ? 'android' : 'ios';
        const providerExamples = examples[provider];
        if (providerExamples && providerExamples.click && providerExamples.click[platformKey]) {
          return providerExamples.click[platformKey];
        }
        return null;
      }

      // Default case for other providers
      return examples[provider].click || null;
    },

    exampleImpressionUrl() {
      const provider = this.offer.attribution_provider;
      if (!provider) return null;

      const examples = this.$t('examples');
      if (!examples || typeof examples !== 'object' || !examples[provider]) {
        return null;
      }

      if (this.providerHasPlatformDifferences(provider)) {
        const platformKey = this.offer.platform_id === 1 ? 'android' : 'ios';
        const providerExamples = examples[provider];
        if (providerExamples && providerExamples.impression && providerExamples.impression[platformKey]) {
          return providerExamples.impression[platformKey];
        }
        return null;
      }

      return examples[provider].impression || null;
    }
  },

  methods: {
    generateTestLinkSuccessHandler: function (responseData) {
      document.getElementById('testLink').style.visibility = 'visible';
      model.test_click_url = responseData.test_click_url;
    },
    detectAttributionProviderSuccessHandler: function (responseData) {
      this.offer.attribution_provider = responseData.attribution_provider;
    },
    clickUrlReservedParameterSuccessHandler: function (responseData) {
      model.click_url_reserved_parameters = responseData.parameters;
    },
    impressionUrlReservedParameterSuccessHandler: function (responseData) {
      model.impression_url_reserved_parameters = responseData.parameters;
    },
    providerHasPlatformDifferences: function (attributionProvider) {
      return attributionProvider === 'AppsFlyer' || attributionProvider === 'Adjust' || attributionProvider === 'Tenjin';
    },
    linkAnalysisSuccessHandler: function (responseData) {
      model.link_analysis_loading = false;
      model.link_analysis_error = null;

      if (typeof responseData === 'string') {
        model.link_analysis_answer = responseData;
        return;
      }

      model.link_analysis_answer =
          (responseData && (responseData.answer || responseData.result || responseData.message)) || null;

      if (!model.link_analysis_answer) {
        model.link_analysis_error = 'No analysis result returned.';
      }
    },
    linkAnalysisErrorHandler: function () {
      model.link_analysis_loading = false;
      model.link_analysis_answer = null;
      model.link_analysis_error = 'Link analysis failed. Please try again.';
    },
    emptyErrorHandler: function () {
    },
    analyzeLinks: function () {
      model.link_analysis_answer = null;
      model.link_analysis_error = null;

      const data = {
        click_url: this.offer.click_url || null,
        impression_url: this.offer.impression_url || null,
        attribution_provider: this.offer.attribution_provider || null,
        app_identifier: this.offer.app_identifier || null,
        example_click_url: this.exampleClickUrl || null,
        example_impression_url: this.exampleImpressionUrl || null,
      };

      model.link_analysis_loading = true;
      session.post('analysis/links', data, this.linkAnalysisSuccessHandler, this.linkAnalysisErrorHandler, model.errors);
    },
    generateTestLink: function () {
      if (model.device_id && this.offer.offer_id) {
        const data = {
          offer_id: this.offer.offer_id,
          platform_id: this.offer.platform_id,
          device_id: model.device_id
        };
        session.post('attribution_test_link', data, this.generateTestLinkSuccessHandler, this.emptyErrorHandler, model.errors)
      }
    },
    validateClickUrl: function () {
      // Keep param fixes; skip reserved-param validation for CPM (server also skips URL validation).
      const isCpm = this.offer.billing_type_id === BillingTypeEnum.CPM.id;
      detectAttributionProvider(this.offer.click_url, this.detectAttributionProviderSuccessHandler, this.emptyErrorHandler);
      if (!isCpm) {
        checkForUrlReservedParameters(this.offer.click_url, this.offer.platform_id, this.clickUrlReservedParameterSuccessHandler, this.emptyErrorHandler);
      }
      if (this.offer.click_url) {
        let fixedUrl = normalizeSingleTapClickUrl(this.offer.click_url);
        if (fixedUrl !== this.offer.click_url) {
          this.offer.click_url = fixedUrl;
        }
      }
    },
    copyUrl: function (url) {
      if (!url) {
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url);
        return;
      }
      const area = document.createElement('textarea');
      area.value = url;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      document.body.removeChild(area);
    },
    validateImpressionUrl: function () {
      const isCpm = this.offer.billing_type_id === BillingTypeEnum.CPM.id;
      detectAttributionProvider(this.offer.impression_url, this.detectAttributionProviderSuccessHandler, this.emptyErrorHandler);
      if (!isCpm) {
        checkForUrlReservedParameters(this.offer.impression_url, this.offer.platform_id, this.impressionUrlReservedParameterSuccessHandler, this.emptyErrorHandler);
      }
      if (this.offer.impression_url) {
        let fixedUrl = ensureAppsFlyerEngagedViewForOdtPid(this.offer.impression_url, this.isSuperUser);
        if (fixedUrl !== this.offer.impression_url) {
          this.offer.impression_url = fixedUrl;
        }
      }
    },
  }
}
</script>
